import {UNIT_BADGE, UNIT_BADGE_SHAPES, UNIT_SIZE_DELTA_BADGE} from '../../configs/unitBadges';

/**
 * Текстуры шевронов отрядов (см. configs/unitBadges). Считаются при первом показе
 * своего режима и без подвыборок: края сглаживает расстояние до контура.
 */

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const mix = (from, to, share) => from.map((channel, index) => channel + (to[index] - channel) * share);

const putPixel = (pixels, offset, [r, g, b], alpha) => {
  pixels[offset] = Math.round(r);
  pixels[offset + 1] = Math.round(g);
  pixels[offset + 2] = Math.round(b);
  pixels[offset + 3] = Math.round(255 * clamp01(alpha));
};

const resolveRim = ({rimLight, rim, rimDark}, heightShare) => (heightShare < 0.5
  ? mix(rimLight, rim, heightShare * 2)
  : mix(rim, rimDark, heightShare * 2 - 1));

const toEvenPixels = (units) => 2 * Math.ceil((units * UNIT_BADGE.pixelsPerUnit) / 2);

/** Ключ текстуры плашки режима */
export const resolveUnitBadgeTextureKey = (mode) => `unit_badge_${mode}`;

/** Ключ текстуры плашки изменения размера */
export const resolveUnitSizeDeltaTextureKey = (kind) => `unit_size_delta_${kind}`;

/**
 * Размер картинки плашки и где ее центр относительно центра ячейки
 * @returns {{width: number, height: number, centerX: number}}
 */
export const resolveUnitBadgeLayout = (mode) => {
  const {left, right} = UNIT_BADGE_SHAPES[mode];
  return {
    width: toEvenPixels(left + right + UNIT_BADGE.marginUnits * 2),
    height: toEvenPixels((UNIT_BADGE.halfHeight + UNIT_BADGE.marginUnits) * 2),
    centerX: (right - left) / 2,
  };
};

export const buildUnitBadgePixels = (mode) => {
  const {left, right, leftWing, rightWing} = UNIT_BADGE_SHAPES[mode];
  const {width, height, centerX} = resolveUnitBadgeLayout(mode);
  const {
    pixelsPerUnit, halfHeight, panelHalfWidth, wingTip, plainTip, rimWidth, dividerHalfWidth, shadowWidth, colors,
  } = UNIT_BADGE;
  const tipLeft = leftWing ? wingTip : plainTip;
  const tipRight = rightWing ? wingTip : plainTip;
  // Сторона острия наклонена: расстояние до нее меньше, чем по оси x
  const normalLeft = 1 / Math.hypot(1, tipLeft / halfHeight);
  const normalRight = 1 / Math.hypot(1, tipRight / halfHeight);
  const pixels = new Uint8Array(width * height * 4);

  for (let py = 0; py < height; py += 1) {
    const y = (py + 0.5 - height / 2) / pixelsPerUnit;
    const absY = Math.abs(y);
    const heightShare = clamp01((y + halfHeight) / (halfHeight * 2));
    for (let px = 0; px < width; px += 1) {
      const x = (px + 0.5 - width / 2) / pixelsPerUnit + centerX;
      const distance = Math.max(
        absY - halfHeight,
        (x - right + tipRight * (absY / halfHeight)) * normalRight,
        (-x - left + tipLeft * (absY / halfHeight)) * normalLeft,
      );
      const offset = (py * width + px) * 4;
      if (distance > 0) {
        const fade = 1 - distance / shadowWidth;
        const edge = clamp01(0.5 - distance * pixelsPerUnit);
        if (edge > 0) {
          putPixel(pixels, offset, resolveRim(colors, heightShare), edge);
        } else if (fade > 0) {
          putPixel(pixels, offset, colors.shadow, 0.6 * fade * fade);
        }
      } else if (-distance < rimWidth) {
        putPixel(pixels, offset, resolveRim(colors, heightShare), 1);
      } else {
        const isWing = Math.abs(x) > panelHalfWidth && ((x > 0 && rightWing) || (x < 0 && leftWing));
        const isDivider = Math.abs(Math.abs(x) - panelHalfWidth) < dividerHalfWidth
          && ((x > 0 && rightWing) || (x < 0 && leftWing));
        let color;
        if (isDivider) {
          color = colors.divider;
        } else if (isWing) {
          color = mix(colors.wingTop, colors.wingBottom, heightShare);
        } else {
          color = mix(colors.panelTop, colors.panelBottom, heightShare);
        }
        putPixel(pixels, offset, color, 1);
      }
    }
  }
  return pixels;
};

/** Размер картинки плашки изменения размера */
export const resolveUnitSizeDeltaLayout = () => ({
  width: toEvenPixels((UNIT_SIZE_DELTA_BADGE.halfWidth + UNIT_BADGE.marginUnits) * 2),
  height: toEvenPixels((UNIT_SIZE_DELTA_BADGE.halfHeight + UNIT_BADGE.marginUnits) * 2),
});

export const buildUnitSizeDeltaPixels = (kind) => {
  const {halfWidth, halfHeight, cornerRadius, rimWidth, styles} = UNIT_SIZE_DELTA_BADGE;
  const {pixelsPerUnit, shadowWidth, colors} = UNIT_BADGE;
  const style = styles[kind];
  const {width, height} = resolveUnitSizeDeltaLayout();
  const pixels = new Uint8Array(width * height * 4);

  for (let py = 0; py < height; py += 1) {
    const y = (py + 0.5 - height / 2) / pixelsPerUnit;
    const heightShare = clamp01((y + halfHeight) / (halfHeight * 2));
    for (let px = 0; px < width; px += 1) {
      const x = (px + 0.5 - width / 2) / pixelsPerUnit;
      const dx = Math.abs(x) - halfWidth + cornerRadius;
      const dy = Math.abs(y) - halfHeight + cornerRadius;
      const distance = Math.hypot(Math.max(dx, 0), Math.max(dy, 0)) + Math.min(Math.max(dx, dy), 0) - cornerRadius;
      const offset = (py * width + px) * 4;
      if (distance > 0) {
        const edge = clamp01(0.5 - distance * pixelsPerUnit);
        const fade = 1 - distance / shadowWidth;
        if (edge > 0) {
          putPixel(pixels, offset, resolveRim(style, heightShare), edge);
        } else if (fade > 0) {
          putPixel(pixels, offset, colors.shadow, 0.6 * fade * fade);
        }
      } else if (-distance < rimWidth) {
        putPixel(pixels, offset, resolveRim(style, heightShare), 1);
      } else if (style.isSolid || y >= 0) {
        const fillShare = style.isSolid ? heightShare : y / halfHeight;
        putPixel(pixels, offset, mix(style.bottomTop, style.bottomBottom, fillShare), 1);
      } else {
        putPixel(pixels, offset, mix(colors.panelTop, colors.panelBottom, heightShare * 2), 1);
      }
    }
  }
  return pixels;
};

const ensureTexture = (scene, key, build, {width, height}, linearFilter) => {
  const textures = scene?.textures;
  if (!textures?.exists || !textures.addUint8Array) {
    return false;
  }
  if (textures.exists(key)) {
    return true;
  }
  const texture = textures.addUint8Array(key, build(), width, height);
  if (texture && linearFilter !== undefined) {
    texture.setFilter?.(linearFilter);
  }
  return Boolean(texture);
};

/** Зарегистрировать текстуру плашки режима, если ее еще нет */
export const ensureUnitBadgeTexture = (scene, mode, {linearFilter} = {}) => ensureTexture(
  scene, resolveUnitBadgeTextureKey(mode), () => buildUnitBadgePixels(mode), resolveUnitBadgeLayout(mode), linearFilter,
);

/** Зарегистрировать текстуру плашки изменения размера, если ее еще нет */
export const ensureUnitSizeDeltaTexture = (scene, kind, {linearFilter} = {}) => ensureTexture(
  scene, resolveUnitSizeDeltaTextureKey(kind), () => buildUnitSizeDeltaPixels(kind), resolveUnitSizeDeltaLayout(), linearFilter,
);
